/**
 * Scene Editor inspector field controls.
 * Visual parity with PlayEngine FieldRow / number / color / string fields —
 * copied for scene editor only; does not import or modify PlayEngine code.
 */

import React, { useEffect, useRef, useState } from "react";
import {
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
} from "@material-ui/core";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import EditIcon from "@material-ui/icons/Edit";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DialogTextEdit from "../dialogTextEdit";

export const PANEL_BG = "#2b2b2e";
export const SECTION_DIVIDER = "#3a3a3f";
export const FIELD_LABEL = "#b8bcc4";
export const BAR_BG = "#3a3a41";

const PLAY_ENGINE_SYNC_DEBOUNCE_MS = 200;
const SCRUB_STEP = 0.01;
const SCRUB_STEP_SHIFT = 0.1;
const ARROW_STEP = 0.01;
const ARROW_STEP_SHIFT = 0.05;
const ARROW_REPEAT_DELAY_MS = 400;
const ARROW_REPEAT_INTERVAL_MS = 60;
const STEP_PIXEL = 14;
const MIXED_VALUE_DASH = "—";

function roundToScrubStep(num, step) {
  const inv = 1 / step;
  return Math.round(num * inv) / inv;
}

/** Inspector row: label ≤20% width (wraps), editor starts at 20% and fills the rest left-aligned. */
export function FieldRow({ label, tooltip, readOnly = false, action = null, mixed = false, children }) {
  const labelText = typeof label === "string" ? label : undefined;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        minHeight: "30px",
        padding: "4px 6px",
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      <div
        title={labelText}
        style={{
          display: "flex",
          alignItems: "center",
          flex: "0 0 20%",
          maxWidth: "20%",
          minWidth: 0,
          minHeight: 24,
          fontSize: "12px",
          color: readOnly ? "#6f7278" : FIELD_LABEL,
          boxSizing: "border-box",
        }}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            lineHeight: 1.35,
            whiteSpace: "normal",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          {label}
        </span>
        {tooltip ? (
          <Tooltip
            title={<span style={{ whiteSpace: "pre-line" }}>{tooltip}</span>}
            arrow
            placement="right"
          >
            <span
              style={{
                display: "inline-flex",
                flexShrink: 0,
                marginLeft: 4,
                cursor: "help",
              }}
            >
              <HelpOutlineIcon style={{ fontSize: 13, color: "#d0d3d8" }} />
            </span>
          </Tooltip>
        ) : null}
      </div>
      <div
        style={{
          flex: "1 1 0",
          minWidth: 0,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        <div style={{ width: "100%", minWidth: 0 }}>{children}</div>
      </div>
      {(action || mixed) ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            alignSelf: "center",
            minHeight: "24px",
            gap: "4px",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {action}
          {mixed && (
            <Tooltip title="Values differ across the selected components" arrow>
              <span
                style={{
                  fontSize: "9px",
                  fontWeight: 600,
                  letterSpacing: "0.4px",
                  textTransform: "uppercase",
                  color: "#c9a25b",
                  cursor: "default",
                }}
              >
                mixed
              </span>
            </Tooltip>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function StringConfigField({ value, disabled, onChange }) {
  const [localValue, setLocalValue] = useState(String(value ?? ""));
  const inputRef = useRef(null);

  useEffect(() => {
    setLocalValue(String(value ?? ""));
  }, [value]);

  return (
    <TextField
      multiline
      minRows={1}
      maxRows={8}
      size="small"
      fullWidth
      disabled={disabled}
      value={localValue}
      inputRef={inputRef}
      InputProps={{ style: { color: "#fff", fontSize: "12px" } }}
      onKeyDown={(e) => {
        // Keep Enter as a line break inside the field; don't let tree/panel shortcuts steal it.
        e.stopPropagation();
      }}
      onChange={(e) => {
        const raw = e.target.value;
        // Convert literally typed \n (backslash + n) into a real newline
        if (raw.includes("\\n")) {
          const cursor = e.target.selectionStart;
          const replaced = raw.replace(/\\n/g, "\n");
          const newCursor = raw.slice(0, cursor).replace(/\\n/g, "\n").length;
          setLocalValue(replaced);
          onChange(replaced);
          requestAnimationFrame(() => {
            if (inputRef.current) inputRef.current.setSelectionRange(newCursor, newCursor);
          });
        } else {
          setLocalValue(raw);
          onChange(raw);
        }
      }}
    />
  );
}

export function ColorConfigField({ value, disabled, onCommit }) {
  const [localValue, setLocalValue] = useState(String(value ?? "#000000"));
  const debounceRef = useRef(null);
  const latestRef = useRef(localValue);
  const committedRef = useRef(normalizeHex(value));
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  useEffect(() => {
    const v = String(value ?? "#000000");
    setLocalValue(v);
    latestRef.current = v;
    committedRef.current = normalizeHex(v);
  }, [value]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
        const pending = latestRef.current;
        // Only flush a complete color on unmount — never a half-typed hex.
        if (/^#[0-9a-f]{6}$/i.test(pending) || /^#[0-9a-f]{3}$/i.test(pending)) {
          onCommitRef.current(expandShortHex(pending));
        }
      }
    };
  }, []);

  function scheduleCommit(hex) {
    const full = expandShortHex(hex);
    latestRef.current = full;
    committedRef.current = full;
    setLocalValue(full);
    if (disabled) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      onCommitRef.current(committedRef.current);
    }, PLAY_ENGINE_SYNC_DEBOUNCE_MS);
  }

  function handleHexChange(e) {
    const v = e.target.value;
    // Allow free editing of # + up to 6 hex digits without committing mid-type.
    if (!/^#[0-9a-f]{0,6}$/i.test(v)) return;
    latestRef.current = v;
    setLocalValue(v);
    // Commit only when the user finishes a full #rrggbb — not on 3–5 digit drafts.
    if (/^#[0-9a-f]{6}$/i.test(v)) {
      scheduleCommit(v);
    }
  }

  function handleHexBlur() {
    const v = String(latestRef.current || "");
    if (/^#[0-9a-f]{6}$/i.test(v)) {
      scheduleCommit(v);
      return;
    }
    if (/^#[0-9a-f]{3}$/i.test(v)) {
      scheduleCommit(v);
      return;
    }
    // Incomplete edit — restore last committed color.
    setLocalValue(committedRef.current);
    latestRef.current = committedRef.current;
  }

  const swatchValue = /^#[0-9a-f]{6}$/i.test(localValue)
    ? localValue
    : committedRef.current;

  return (
    <TextField
      size="small"
      fullWidth
      value={localValue}
      disabled={disabled}
      onKeyDown={(e) => e.stopPropagation()}
      onChange={handleHexChange}
      onBlur={handleHexBlur}
      InputProps={{
        style: {
          color: "#aaa",
          fontSize: "12px",
          fontFamily: "monospace",
          height: 24,
        },
        startAdornment: (
          <InputAdornment position="start" style={{ marginLeft: 4, marginRight: 0, height: "auto" }}>
            <input
              type="color"
              className="scene-editor-color-swatch"
              value={swatchValue}
              disabled={disabled}
              title="Pick color"
              style={{
                width: 18,
                height: 18,
                boxSizing: "border-box",
                border: "1px solid #3a3a3f",
                borderRadius: 3,
                padding: 0,
                background: "transparent",
                cursor: disabled ? "default" : "pointer",
                display: "block",
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onChange={(e) => scheduleCommit(e.target.value)}
            />
          </InputAdornment>
        ),
      }}
    />
  );
}

function expandShortHex(hex) {
  const v = String(hex ?? "");
  if (/^#[0-9a-f]{6}$/i.test(v)) return v.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(v)) {
    const h = v.slice(1);
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toLowerCase();
  }
  return "#000000";
}

function normalizeHex(value) {
  const v = String(value ?? "#000000");
  if (/^#[0-9a-f]{3,6}$/i.test(v)) return expandShortHex(v);
  return "#000000";
}

const PREVIEW_IFRAME_ID = "gamePreviewIframe";
const SCRUB_CURSOR_ID = "scene-editor-scrub-cursor";

function setPreviewIframePointerEvents(enabled) {
  const iframe = document.getElementById(PREVIEW_IFRAME_ID);
  if (!iframe) return;
  iframe.style.pointerEvents = enabled ? "" : "none";
}

function wrapCoord(value, size) {
  if (!Number.isFinite(size) || size <= 0) return value;
  const mod = value % size;
  return mod < 0 ? mod + size : mod;
}

/** System-like ew-resize stand-in (pointer-lock hides the real OS cursor). */
function ensureScrubCursor() {
  let el = document.getElementById(SCRUB_CURSOR_ID);
  if (el) return el;
  el = document.createElement("div");
  el.id = SCRUB_CURSOR_ID;
  el.setAttribute("aria-hidden", "true");
  Object.assign(el.style, {
    position: "fixed",
    left: "0",
    top: "0",
    width: "24px",
    height: "24px",
    marginLeft: "-12px",
    marginTop: "-12px",
    pointerEvents: "none",
    zIndex: "2147483647",
    willChange: "transform",
    // Keep the normal resize glyph — not a custom badge/icon.
    cursor: "ew-resize",
    backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">' +
        '<path d="M7 12H17M9 9L6 12L9 15M15 9L18 12L15 15" fill="none" stroke="black" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>' +
        '<path d="M7 12H17M9 9L6 12L9 15M15 9L18 12L15 15" fill="none" stroke="white" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>' +
      "</svg>"
    )}")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundSize: "24px 24px",
  });
  document.body.appendChild(el);
  return el;
}

function moveScrubCursor(x, y) {
  const el = ensureScrubCursor();
  el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
}

function hideScrubCursor() {
  const el = document.getElementById(SCRUB_CURSOR_ID);
  if (el) el.remove();
}

export function NumberConfigField({
  value,
  disabled,
  onChange,
  label,
  mixed = false,
  step: stepProp,
  min,
  max,
}) {
  // Local display string so the user can type intermediary states freely.
  const [localValue, setLocalValue] = useState(String(value ?? ""));
  const [focused, setFocused] = useState(false);
  const [gesturing, setGesturing] = useState(false);
  const inputRef = useRef(null);
  const currentNumRef = useRef(0);
  const shiftHeldRef = useRef(false);
  const arrowRepeatRef = useRef(null);

  const showsDash = mixed && !focused && !gesturing;

  const inputCursor = disabled ? "not-allowed" : focused ? "text" : "ew-resize";

  const arrowStep = Number.isFinite(stepProp) && stepProp > 0 ? stepProp : ARROW_STEP;
  const arrowStepShift = Number.isFinite(stepProp) && stepProp > 0 ? stepProp * 10 : ARROW_STEP_SHIFT;
  const scrubStep = Number.isFinite(stepProp) && stepProp > 0 ? stepProp : SCRUB_STEP;
  const scrubStepShift =
    Number.isFinite(stepProp) && stepProp > 0 ? stepProp * 10 : SCRUB_STEP_SHIFT;

  function clampNum(num) {
    let v = num;
    if (!Number.isFinite(v)) return v;
    if (Number.isFinite(min)) v = Math.max(min, v);
    if (Number.isFinite(max)) v = Math.min(max, v);
    return v;
  }

  function setInputCursor(cursor) {
    if (inputRef.current) inputRef.current.style.cursor = cursor;
  }

  // Sync when the prop value changes from outside (e.g. version switch).
  // While scrubbing/arrow-holding, local state owns the display — ignore stale props.
  useEffect(() => {
    if (gesturing) return;
    setLocalValue(String(value ?? ""));
    const n = Number(value);
    currentNumRef.current = isFinite(n) ? n : 0;
  }, [value, gesturing]);

  useEffect(() => {
    setInputCursor(inputCursor);
  }, [inputCursor]);

  function clearArrowRepeat() {
    const active = arrowRepeatRef.current;
    if (!active) return;
    if (active.timeout) clearTimeout(active.timeout);
    if (active.interval) clearInterval(active.interval);
    if (active.cleanupListeners) active.cleanupListeners();
    arrowRepeatRef.current = null;
  }

  function parseScrubBase() {
    const trimmed = localValue.trim();
    if (trimmed === "" || trimmed === "-" || trimmed.endsWith(".")) {
      const fallback = Number(value);
      return isFinite(fallback) ? fallback : 0;
    }
    const num = Number(trimmed);
    return isFinite(num) ? num : 0;
  }

  /**
   * Scrubbing and the arrow buttons are gestures — "move it by this much". The
   * flag says so, which is what lets a multi-component edit shift each one from
   * where it already was. Typing a number is not a gesture: it means "be this",
   * and every selected component takes it as written.
   */
  function commitScrubValue(num, step, opts = {}) {
    const rounded = clampNum(roundToScrubStep(num, step));
    const unchanged = rounded === currentNumRef.current;
    currentNumRef.current = rounded;
    setLocalValue(String(rounded));
    if (unchanged && !opts.gestureEnd) return;
    onChange(rounded, { gesture: true, step, ...opts });
  }

  function stepByArrow(direction, shiftKey) {
    const step = shiftKey ? arrowStepShift : arrowStep;
    commitScrubValue(currentNumRef.current + direction * step, step);
  }

  function handleArrowPointerDown(direction, e) {
    if (disabled || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    clearArrowRepeat();
    setGesturing(true);
    shiftHeldRef.current = e.shiftKey;
    stepByArrow(direction, shiftHeldRef.current);

    const trackShift = (ev) => {
      shiftHeldRef.current = ev.shiftKey;
    };
    document.addEventListener("keydown", trackShift);
    document.addEventListener("keyup", trackShift);

    const cleanupListeners = () => {
      document.removeEventListener("keydown", trackShift);
      document.removeEventListener("keyup", trackShift);
      document.removeEventListener("mouseup", onPointerUp);
    };

    function onPointerUp() {
      setGesturing(false);
      clearArrowRepeat();
      onChange(clampNum(currentNumRef.current), { gesture: true, gestureEnd: true });
    }

    const timeout = setTimeout(() => {
      const interval = setInterval(() => {
        stepByArrow(direction, shiftHeldRef.current);
      }, ARROW_REPEAT_INTERVAL_MS);
      if (arrowRepeatRef.current) arrowRepeatRef.current.interval = interval;
    }, ARROW_REPEAT_DELAY_MS);

    document.addEventListener("mouseup", onPointerUp);
    arrowRepeatRef.current = { timeout, interval: null, cleanupListeners };
  }

  useEffect(() => () => clearArrowRepeat(), []);

  function handleScrubMouseDown(e) {
    if (disabled || e.button !== 0) return;

    const startX = e.clientX;
    const startY = e.clientY;
    const startValue = parseScrubBase();
    let scrubbing = false;
    let ended = false;
    let lockEngaged = false;
    // Accumulated horizontal movement — keeps growing past the window edge when
    // pointer lock is active (Figma-style unbounded scrub).
    let accumX = 0;
    let cursorX = startX;
    let cursorY = startY;
    let scrubRaf = 0;
    let pendingShiftKey = false;

    function applyAccum(shiftKey) {
      const step = shiftKey ? scrubStepShift : scrubStep;
      commitScrubValue(startValue + (accumX / STEP_PIXEL) * step, step);
    }

    function scheduleScrubApply(shiftKey) {
      pendingShiftKey = shiftKey;
      if (scrubRaf) return;
      scrubRaf = requestAnimationFrame(() => {
        scrubRaf = 0;
        applyAccum(pendingShiftKey);
      });
    }

    function updateWrappedCursor(movementX, movementY) {
      cursorX = wrapCoord(cursorX + (movementX || 0), window.innerWidth);
      cursorY = wrapCoord(cursorY + (movementY || 0), window.innerHeight);
      moveScrubCursor(cursorX, cursorY);
    }

    function beginScrub(initialDeltaX) {
      scrubbing = true;
      setGesturing(true);
      setPreviewIframePointerEvents(false);
      document.body.style.cursor = "none";
      document.body.style.userSelect = "none";
      setInputCursor("none");
      inputRef.current?.blur();
      accumX = initialDeltaX;
      cursorX = wrapCoord(startX + initialDeltaX, window.innerWidth);
      cursorY = startY;
      moveScrubCursor(cursorX, cursorY);

      // Pointer lock unlocks scrub from the monitor edge: movementX keeps
      // reporting after the physical cursor would have stopped at the bezel.
      // OS cursor is hidden by the browser — we draw a wrapping ew-resize stand-in.
      const lockTarget = document.documentElement;
      try {
        const maybePromise = lockTarget.requestPointerLock({ unadjustedMovement: true });
        if (maybePromise && typeof maybePromise.catch === "function") {
          maybePromise.catch(() => {
            try {
              lockTarget.requestPointerLock();
            } catch (_) {
              /* ignore — fall back to clientX tracking */
            }
          });
        }
      } catch (_) {
        try {
          lockTarget.requestPointerLock();
        } catch (__) {
          /* ignore */
        }
      }

      scheduleScrubApply(false);
    }

    function onMouseMove(moveEvent) {
      if (!scrubbing) {
        const provisional = moveEvent.clientX - startX;
        if (Math.abs(provisional) < 3) return;
        beginScrub(provisional);
        return;
      }

      moveEvent.preventDefault();
      if (lockEngaged || document.pointerLockElement) {
        lockEngaged = true;
        accumX += moveEvent.movementX || 0;
        updateWrappedCursor(moveEvent.movementX, moveEvent.movementY);
      } else {
        // Lock denied / not yet granted: track clientX (edge-limited).
        accumX = moveEvent.clientX - startX;
        cursorX = moveEvent.clientX;
        cursorY = moveEvent.clientY;
        moveScrubCursor(cursorX, cursorY);
      }
      scheduleScrubApply(moveEvent.shiftKey);
    }

    function onMouseUp() {
      if (ended) return;
      ended = true;
      setGesturing(false);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("pointerlockchange", onPointerLockChange);
      window.removeEventListener("blur", onMouseUp);
      if (scrubRaf) {
        cancelAnimationFrame(scrubRaf);
        scrubRaf = 0;
        applyAccum(pendingShiftKey);
      }
      if (document.pointerLockElement) {
        try {
          document.exitPointerLock();
        } catch (_) {
          /* ignore */
        }
      }
      hideScrubCursor();
      setPreviewIframePointerEvents(true);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      const isFocused = document.activeElement === inputRef.current;
      setInputCursor(disabled ? "not-allowed" : isFocused ? "text" : "ew-resize");
      if (scrubbing) {
        onChange(clampNum(currentNumRef.current), { gesture: true, gestureEnd: true });
      }
    }

    function onPointerLockChange() {
      if (document.pointerLockElement) {
        lockEngaged = true;
        return;
      }
      // Esc (or browser UI) exits lock mid-scrub — finish the gesture cleanly.
      if (scrubbing && !ended) {
        onMouseUp();
      }
    }

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("pointerlockchange", onPointerLockChange);
    window.addEventListener("blur", onMouseUp);
  }

  function handleChange(e) {
    const str = e.target.value;
    setLocalValue(str);

    const trimmed = str.trim();
    if (trimmed === "") return;             // empty — don't fire
    if (trimmed.endsWith(".")) return;      // incomplete decimal like "1."
    if (trimmed === "-") return;            // bare minus
    const num = Number(trimmed);
    if (!isFinite(num)) return;             // NaN or ±Infinity
    const clamped = clampNum(num);
    currentNumRef.current = clamped;
    if (clamped !== num) setLocalValue(String(clamped));
    onChange(clamped);
  }

  return (
    <TextField
      type="text"
      inputMode="decimal"
      size="small"
      fullWidth
      disabled={disabled}
      value={showsDash ? MIXED_VALUE_DASH : localValue}
      inputRef={inputRef}
      InputProps={{
        style: { color: showsDash ? "#8a8d93" : "#fff", fontSize: "12px" },
        startAdornment: label ? (
          <InputAdornment position="start" style={{ margin: "0 0 0 4px" }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: "#8a8d93", textTransform: "lowercase" }}>
              {label}
            </span>
          </InputAdornment>
        ) : undefined,
        endAdornment: !disabled ? (
          <InputAdornment
            position="end"
            style={{ alignSelf: "center", marginRight: 6, height: "auto" }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                lineHeight: 0,
              }}
            >
              <IconButton
                size="small"
                tabIndex={-1}
                title={`Increment (+${arrowStep}, Shift +${arrowStepShift})`}
                style={{ padding: 0, width: 16, height: 10, color: "#aaa" }}
                onMouseDown={(e) => handleArrowPointerDown(1, e)}
              >
                <KeyboardArrowUpIcon style={{ fontSize: 13 }} />
              </IconButton>
              <IconButton
                size="small"
                tabIndex={-1}
                title={`Decrement (−${arrowStep}, Shift −${arrowStepShift})`}
                style={{ padding: 0, width: 16, height: 10, color: "#aaa" }}
                onMouseDown={(e) => handleArrowPointerDown(-1, e)}
              >
                <KeyboardArrowDownIcon style={{ fontSize: 13 }} />
              </IconButton>
            </div>
          </InputAdornment>
        ) : undefined,
      }}
      inputProps={{
        readOnly: !focused,
        onMouseDown: handleScrubMouseDown,
        style: { cursor: inputCursor },
      }}
      style={{ width: "100%" }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (disabled) return;
        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          e.preventDefault();
          stepByArrow(e.key === "ArrowUp" ? 1 : -1, e.shiftKey);
        }
      }}
      onFocus={() => {
        setFocused(true);
        // Typing into a dash would produce "-5" out of a "5" keystroke, and the
        // first component's number is not what the field was showing either —
        // so an edit starts from empty and whatever is typed lands on them all.
        if (mixed) setLocalValue("");
      }}
      onBlur={() => {
        setFocused(false);
        // Half-typed text ("1.", "-") never reached the components; the field
        // goes back to reporting them rather than keeping it on screen.
        setLocalValue(String(value ?? ""));
      }}
      onChange={handleChange}
    />
  );
}


export function BooleanConfigField({ value, disabled, onChange }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", minHeight: 24, width: "100%" }}>
      <Switch
        size="small"
        checked={Boolean(value)}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        color="primary"
        style={{ margin: 0 }}
      />
    </div>
  );
}

/**
 * Grid / drop strings (editorGridArea). These are long single-line strings that are
 * only readable once broken on their separator, so they keep the classic panel's
 * arrange + separator + Save dialog — repainted in the inspector palette via the
 * dialog's sceneEditor variant. The row itself stays inspector-shaped: a read-only
 * monospace preview of the current value plus an edit affordance.
 */
export function GridAreaConfigField({ value, disabled, label, storageKey, onCommit, onPreview }) {
  const [isOpen, setIsOpen] = useState(false);
  const preview = typeof value === "string" ? value : JSON.stringify(value ?? "");
  const open = () => {
    if (!disabled) setIsOpen(true);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", minWidth: 0 }}>
      <div
        title={preview}
        onClick={open}
        style={{
          flex: 1,
          minWidth: 0,
          height: 26,
          display: "flex",
          alignItems: "center",
          padding: "0 8px",
          boxSizing: "border-box",
          backgroundColor: BAR_BG,
          border: `1px solid ${SECTION_DIVIDER}`,
          borderRadius: 4,
          color: disabled ? "#6f7278" : "#fff",
          fontSize: "12px",
          fontFamily: "monospace",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          cursor: disabled ? "default" : "pointer",
        }}
      >
        {preview || MIXED_VALUE_DASH}
      </div>
      <Tooltip title="Edit">
        <span>
          <IconButton
            size="small"
            disabled={disabled}
            onClick={open}
            style={{ color: FIELD_LABEL, padding: 4 }}
          >
            <EditIcon style={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
      {isOpen ? (
        <DialogTextEdit
          open
          variant="sceneEditor"
          onClose={() => setIsOpen(false)}
          value={value}
          setValue={onCommit}
          livePreview={onPreview}
          label={label}
          storageKey={storageKey}
        />
      ) : null}
    </div>
  );
}

export function SectionHeader({ title, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", margin: "12px 6px 6px" }}>
      <span style={{ flex: 1, height: "1px", backgroundColor: SECTION_DIVIDER }} />
      <span
        style={{
          fontSize: "11px",
          letterSpacing: "0.6px",
          textTransform: "uppercase",
          color: "#8a8d93",
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {title}
      </span>
      <span style={{ flex: 1, height: "1px", backgroundColor: SECTION_DIVIDER }} />
      {right}
    </div>
  );
}
